import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { AccountList } from "@/components/account-list/AccountList";
import { ErrorPage } from "@/components/error-page/ErrorPage";
import { NavButtons } from "@/components/general/nav-buttons/NavButtons";
import { Section } from "@/components/section/Section";
import { useAccount } from "@/hooks/deposit/useAccount";
import { useLogger } from "@/hooks/useLogger";
import { useTradingAccounts } from "@/hooks/useTradingAccounts";
import { DepositNotAvailable } from "@/pages/deposit/DepositNotAvailable";
import { GLOBAL_NAVIGATION_URLS, globalNavigation } from "@/utils/constants";
import { ECashierFlows, ERoutes } from "@/utils/enums";
import { serializeError } from "@/utils/log";
import style from "./Accounts.module.css";

export const Accounts = () => {
  const { t } = useTranslation();
  const logger = useLogger();
  const navigate = useNavigate();
  const {
    data: allTradingAccounts,
    error,
    isLoading,
    refetch,
  } = useTradingAccounts();

  const tradingAccountList = allTradingAccounts?.filter(
    (account) => account.isEnabled && account.isFundable
  );
  const { setSelectedAccount, selectedAccount } = useAccount();
  const { scaAccountLoginId } = useParams();

  // Refetch account balances on mount since the query in CustomerDataProvider never unmounts
  useEffect(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    if (!isLoading && tradingAccountList && scaAccountLoginId) {
      const account = tradingAccountList.find(
        (account) => account.accountLoginId === scaAccountLoginId
      );
      if (account) {
        setSelectedAccount(account);
        navigate(`../${account.accountLoginId}/${ERoutes.ChooseMethod}`);
      } else {
        logger.warn("Can't find trading account by id", { scaAccountLoginId });
        navigate(`../${ERoutes.Accounts}`);
      }
    }
  }, [
    scaAccountLoginId,
    tradingAccountList,
    isLoading,
    setSelectedAccount,
    navigate,
    logger,
  ]);

  if (error) {
    logger.error("Failed to load deposit trading accounts", {
      error: serializeError(error),
    });
    return (
      <ErrorPage buttonAction={() => navigate(`/${ECashierFlows.Deposit}`)} />
    );
  }

  if (!isLoading && !tradingAccountList?.length) {
    logger.error("Deposit not available, no valid trading accounts found");
    return <DepositNotAvailable />;
  }

  return (
    <div className={"ps-flex ps-flex-col ps-gap-8"}>
      <Section title={t("deposit.accounts.select_account")}>
        <AccountList
          isLoading={isLoading}
          accountList={tradingAccountList ?? []}
          selectedAccountChange={setSelectedAccount}
          initialSelectedAccount={selectedAccount}
        />
      </Section>
      <NavButtons
        className={style.navButtons}
        nextLabel={t("global.nav_buttons.next")}
        nextDisabled={!selectedAccount}
        nextAction={() =>
          navigate(
            `../${selectedAccount?.accountLoginId}/${ERoutes.ChooseMethod}`
          )
        }
        backAction={() =>
          globalNavigation(GLOBAL_NAVIGATION_URLS.DASHBOARD, true)
        }
      />
    </div>
  );
};
